<script>
import { mapGetters } from 'vuex';
import { appKind, workspaceRequestMatches } from 'dashboard/helper/reelWorkspace.mjs';
import ConversationHeader from './ConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';

export default {
  components: {
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return { activeIndex: 0 };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
      reelAccountId: 'getCurrentAccountId',
    }),
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.dashboardApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + 1,
          name: appKind(dashboardApp, location.origin) ? this.$t(`REEL_UI.${appKind(dashboardApp, location.origin).toUpperCase()}`) : dashboardApp.title,
        })),
      ];
    },
    reelOtherTabs() {
      return this.dashboardAppTabs.filter(tab => tab.index > 0 && !appKind(this.dashboardApps[tab.index - 1], location.origin));
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
  },
  watch: {
    activeIndex() { this.publishReelWorkspace(); },
    'currentChat.inbox_id': {
      immediate: true,
      handler(inboxId) {
        if (inboxId) {
          this.$store.dispatch('inboxAssignableAgents/fetch', {
            inboxIds: [inboxId],
            includeAgentBots: true,
          });
        }
      },
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
    window.addEventListener('reelnearby:open-dashboard-app', this.openReelWorkspace);
  },
  unmounted() { window.removeEventListener('reelnearby:open-dashboard-app', this.openReelWorkspace); },
  methods: {
    openReelWorkspace(event) {
      if (!workspaceRequestMatches(event.detail, this.currentChat, this.reelAccountId, this.dashboardApps, location.origin)) return;
      if (String(this.$route.params.conversation_id) !== String(this.currentChat.id)) return;
      this.activeIndex = event.detail.kind === 'conversations' ? 0 : this.dashboardApps.findIndex(app => String(app.id) === String(event.detail.appId)) + 1;
    },
    publishReelWorkspace() {
      window.dispatchEvent(new CustomEvent('reelnearby:workspace-changed', { detail: {
        accountId: this.reelAccountId, conversationId: this.currentChat.id,
        kind: this.activeIndex === 0 ? 'conversations' : appKind(this.dashboardApps[this.activeIndex - 1], location.origin),
      } }));
    },
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col min-w-0 w-full bg-n-surface-1 relative"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak': !isOnExpandedLayout,
    }"
  >
    <ConversationHeader
      v-if="currentChat.id"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
      :class="{
        'border-b border-b-n-weak !pt-2': !dashboardApps.length,
      }"
    />
    <div
      v-if="currentChat.id && (activeIndex || reelOtherTabs.length)"
      data-reel-workspace-header
      class="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b border-n-weak bg-n-solid-1"
    >
      <h2 class="text-sm font-semibold text-n-slate-12 m-0" data-reel-workspace-title>{{ dashboardAppTabs[activeIndex]?.name }}</h2>
      <div class="flex items-center gap-2">
        <select v-if="reelOtherTabs.length" :value="reelOtherTabs.some(tab => tab.index === activeIndex) ? activeIndex : ''" :aria-label="$t('REEL_UI.OTHER_APPS')" class="max-w-40 min-h-10 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus-visible:ring-2 focus-visible:ring-n-blue-11" @change="onDashboardAppTabChange(Number($event.target.value))">
          <option value="" disabled>{{ $t('REEL_UI.OTHER_APPS') }}</option>
          <option v-for="tab in reelOtherTabs" :key="tab.key" :value="tab.index">{{ tab.name }}</option>
        </select>
        <button v-if="activeIndex" type="button" data-reel-return-messages class="flex items-center gap-2 min-h-10 px-3 text-sm font-medium rounded-lg text-n-blue-11 hover:bg-n-slate-3 focus-visible:ring-2 focus-visible:ring-n-blue-11" @click="onDashboardAppTabChange(0)">
          <span class="i-lucide-arrow-left size-4" aria-hidden="true" />{{ $t('REEL_UI.BACK_TO_MESSAGES') }}
        </button>
      </div>
    </div>
    <div v-show="!activeIndex" class="flex h-full min-h-0 m-0">
      <MessagesView
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-inbox-view="isInboxView"
      />
      <EmptyState
        v-if="!currentChat.id && !isInboxView"
        :is-on-expanded-layout="isOnExpandedLayout"
      />
      <slot />
    </div>
    <DashboardAppFrame
      v-for="(dashboardApp, index) in dashboardApps"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 1 === index"
      :config="dashboardApps[index].content"
      :position="index"
      :current-chat="currentChat"
    />
  </div>
</template>
